// תבניות קומפוזיציה דטרמיניסטיות לכל משפחת יחס ממדים, ובדיקות גאומטריה.
// התבניות משמשות כ-fixture קבוע, כנקודת פתיחה ל-AI, וכ-fallback כשה-AI לא זמין.
import { familyOf, SCHEMA_VERSION, type BannerDoc, type Family, type Layer } from '@bb/schema';

export interface TemplateInput {
  width: number;
  height: number;
  colors: { background: string; text: string; cta: string; ctaText: string; accent?: string };
  logoAssetId: string | null;
  imageAssetId: string | null;
  fontAssetId: string | null;
  fontFamily: string;
  headline: string;
  subline?: string;
  cta: string;
  disclaimer?: string;
  direction?: 'rtl' | 'ltr';
  durationMs?: number;
  loops?: number;
}

type Box = { x: number; y: number; w: number; h: number };
type Slots = { logo: Box; headline: Box; subline: Box; cta: Box; image: Box | null; disclaimer: Box };

/** חוקי מיקום לכל משפחה — ביחס (0..1) למידות הבאנר */
function slotsFor(family: Family, W: number, H: number, hasImage: boolean): Slots {
  const b = (x: number, y: number, w: number, h: number): Box => ({ x: Math.round(x * W), y: Math.round(y * H), w: Math.round(w * W), h: Math.round(h * H) });
  switch (family) {
    case 'thin': // 728x90, 970x90, 320x50 — רצועה: לוגו בצד ימין, טקסט באמצע, CTA משמאל
      return {
        logo: b(0.83, 0.15, 0.15, 0.7),
        headline: b(0.3, 0.06, 0.5, 0.42),
        subline: b(0.3, 0.5, 0.5, 0.28),
        cta: b(0.03, 0.25, 0.22, 0.5),
        image: null,
        disclaimer: b(0.3, 0.8, 0.5, 0.18),
      };
    case 'horizontal': // 300x250 לא כאן; 336x280 ריבועי; 600x300, 1200x628
      return {
        logo: b(0.74, 0.08, 0.22, 0.2),
        headline: b(hasImage ? 0.42 : 0.06, 0.32, hasImage ? 0.54 : 0.88, 0.3),
        subline: b(hasImage ? 0.42 : 0.06, 0.6, hasImage ? 0.54 : 0.88, 0.14),
        cta: b(hasImage ? 0.42 : 0.06, 0.78, 0.28, 0.14),
        image: hasImage ? b(0, 0, 0.38, 1) : null,
        disclaimer: b(0.06, 0.93, 0.88, 0.06),
      };
    case 'vertical': // 160x600, 300x600, 120x600
      return {
        logo: b(0.15, 0.03, 0.7, 0.1),
        headline: b(0.08, 0.16, 0.84, 0.22),
        subline: b(0.08, 0.38, 0.84, 0.1),
        cta: b(0.12, 0.86, 0.76, 0.07),
        image: hasImage ? b(0, 0.5, 1, 0.33) : null,
        disclaimer: b(0.05, 0.95, 0.9, 0.04),
      };
    case 'square':
    default: // 300x250, 250x250, 336x280
      return {
        logo: b(0.62, 0.06, 0.32, 0.16),
        headline: b(0.07, 0.26, 0.86, 0.26),
        subline: b(0.07, 0.52, 0.86, 0.12),
        cta: b(0.25, 0.72, 0.5, 0.14),
        image: hasImage ? b(0, 0, 1, 1) : null,
        disclaimer: b(0.05, 0.9, 0.9, 0.08),
      };
  }
}

function fitFont(box: Box, text: string, lines: number, maxPx: number): number {
  // הערכה שמרנית: רוחב תו ממוצע ~0.55em
  const byHeight = box.h / (lines * 1.15);
  const charsPerLine = Math.max(1, Math.ceil(text.length / lines));
  // עברית מודגשת רחבה יותר מהממוצע — הערכה שמרנית; התאמה מדויקת נעשית במדידה בדפדפן
  const byWidth = box.w / (charsPerLine * 0.62);
  return Math.max(8, Math.floor(Math.min(byHeight, byWidth, maxPx)));
}

export function templateFor(input: TemplateInput): BannerDoc {
  const { width: W, height: H } = input;
  const family = familyOf(W, H);
  const hasImage = !!input.imageAssetId;
  const s = slotsFor(family, W, H, hasImage);
  const dir = input.direction ?? 'rtl';
  const font = { fontAssetId: input.fontAssetId, fontFamily: input.fontFamily, direction: dir, letterSpacing: 0, copyKey: null as string | null, locked: false };
  const layers: Layer[] = [];
  const assetIds = new Set<string>();
  if (input.fontAssetId) assetIds.add(input.fontAssetId);
  let z = 1;
  if (s.image && input.imageAssetId) {
    assetIds.add(input.imageAssetId);
    layers.push({ id: 'image', name: 'תמונה', type: 'image', role: 'image', x: s.image.x, y: s.image.y, width: s.image.w, height: s.image.h, zIndex: z++, rotation: 0, opacity: 1, visible: true, locked: false, startMs: 0, endMs: null, content: { assetId: input.imageAssetId, fit: 'cover', focalX: 0.5, focalY: 0.5 } });
    if (family === 'square') {
      // שכבת הצללה לקריאות טקסט מעל תמונה מלאה
      layers.push({ id: 'scrim', name: 'הצללה', type: 'shape', role: 'decor', x: 0, y: Math.round(H * 0.2), width: W, height: Math.round(H * 0.8), zIndex: z++, rotation: 0, opacity: 0.82, visible: true, locked: false, startMs: 0, endMs: null, content: { fill: input.colors.background, radius: 0, borderColor: null, borderWidth: 0 } });
    }
  }
  const thin = family === 'thin';
  const hl = s.headline;
  layers.push({ id: 'headline', name: 'כותרת', type: 'text', role: 'headline', x: hl.x, y: hl.y, width: hl.w, height: hl.h, zIndex: z++, rotation: 0, opacity: 1, visible: true, locked: false, startMs: 300, endMs: null,
    content: { ...font, text: input.headline, fontWeight: 800, fontSize: fitFont(hl, input.headline, thin ? 1 : 2, family === 'vertical' ? 30 : 34), lineHeight: 1.1, align: 'center', verticalAlign: 'middle', color: input.colors.text } });
  if (input.subline) {
    const sb = s.subline;
    layers.push({ id: 'subline', name: 'משנה', type: 'text', role: 'subline', x: sb.x, y: sb.y, width: sb.w, height: sb.h, zIndex: z++, rotation: 0, opacity: 1, visible: true, locked: false, startMs: 900, endMs: null,
      content: { ...font, text: input.subline, fontWeight: 400, fontSize: fitFont(sb, input.subline, thin ? 1 : 2, 18), lineHeight: 1.2, align: 'center', verticalAlign: 'middle', color: input.colors.text } });
  }
  const cb = s.cta;
  layers.push({ id: 'cta', name: 'CTA', type: 'button', role: 'cta', x: cb.x, y: cb.y, width: cb.w, height: cb.h, zIndex: z++, rotation: 0, opacity: 1, visible: true, locked: false, startMs: 1500, endMs: null,
    content: { ...font, text: input.cta, fontWeight: 700, fontSize: fitFont({ ...cb, w: cb.w * 0.85, h: cb.h * 0.8 }, input.cta, 1, 18), lineHeight: 1, align: 'center', verticalAlign: 'middle', color: input.colors.ctaText, fill: input.colors.cta, radius: Math.round(cb.h / 4) } });
  if (input.logoAssetId) {
    assetIds.add(input.logoAssetId);
    const lb = s.logo;
    layers.push({ id: 'logo', name: 'לוגו', type: 'image', role: 'logo', x: lb.x, y: lb.y, width: lb.w, height: lb.h, zIndex: z++, rotation: 0, opacity: 1, visible: true, locked: false, startMs: 0, endMs: null, content: { assetId: input.logoAssetId, fit: 'contain', focalX: 0.5, focalY: 0.5 } });
  }
  if (input.disclaimer) {
    const db = s.disclaimer;
    layers.push({ id: 'disclaimer', name: 'גילוי נאות', type: 'text', role: 'disclaimer', x: db.x, y: db.y, width: db.w, height: db.h, zIndex: z++, rotation: 0, opacity: 1, visible: true, locked: false, startMs: 0, endMs: null,
      content: { ...font, text: input.disclaimer, fontWeight: 400, fontSize: Math.max(8, Math.min(10, Math.floor(db.h * 0.8))), lineHeight: 1.1, align: 'center', verticalAlign: 'middle', color: input.colors.text } });
  }
  const slideFrom = dir === 'rtl' ? 'right' : 'left';
  return {
    schemaVersion: SCHEMA_VERSION,
    width: W, height: H,
    durationMs: input.durationMs ?? 6000,
    loops: input.loops ?? 2,
    endFrameMs: null,
    background: { color: input.colors.background, assetId: null, fit: 'cover' },
    layers,
    animationTracks: [
      { layerId: 'headline', enter: { type: 'slide', from: slideFrom, distance: Math.round(Math.min(W, H) * 0.15), durationMs: 500, easing: 'easeOut' }, exit: null, keyframes: [] },
      ...(input.subline ? [{ layerId: 'subline', enter: { type: 'fade' as const, from: 'right' as const, distance: 0, durationMs: 500, easing: 'easeOut' as const }, exit: null, keyframes: [] }] : []),
      { layerId: 'cta', enter: { type: 'scale', from: 'right', distance: 0, durationMs: 400, easing: 'easeOut' }, exit: null, keyframes: [] },
    ],
    assetIds: [...assetIds],
    meta: { family },
  };
}

export interface GeometryIssue {
  code: 'out_of_bounds' | 'overlap' | 'small_text' | 'logo_cropped' | 'logo_too_small' | 'text_may_overflow';
  severity: 'error' | 'warning';
  layerIds: string[];
  message: string;
}

/** בדיקות גאומטריה סטטיות על המצב הסופי של המסמך (ללא אנימציה). */
export function geometryIssues(doc: BannerDoc, opts: { minFontPx: number; logoMinWidthPx?: number | null; allowOverlapRoles?: string[] }): GeometryIssue[] {
  const out: GeometryIssue[] = [];
  const vis = doc.layers.filter((l) => l.visible);
  for (const l of vis) {
    const inside = l.x >= -0.5 && l.y >= -0.5 && l.x + l.width <= doc.width + 0.5 && l.y + l.height <= doc.height + 0.5;
    if (!inside && l.role !== 'background' && l.role !== 'image' && l.role !== 'decor') {
      out.push({ code: l.role === 'logo' ? 'logo_cropped' : 'out_of_bounds', severity: l.role === 'logo' || l.role === 'cta' ? 'error' : 'warning', layerIds: [l.id], message: l.role === 'logo' ? `הלוגו (${l.id}) נחתך בגבול הבאנר` : `השכבה ${l.id} חורגת מגבולות הבאנר` });
    }
    if ((l.type === 'text' || l.type === 'button') && l.content.fontSize < opts.minFontPx) {
      out.push({ code: 'small_text', severity: 'warning', layerIds: [l.id], message: `גודל טקסט ${l.content.fontSize}px קטן מהמינימום של המותג (${opts.minFontPx}px) בשכבה ${l.id}` });
    }
    if ((l.type === 'text' || l.type === 'button') && l.content.text) {
      // הערכה גסה; המדידה האמיתית נעשית בבדיקת הדפדפן
      const lines = Math.max(1, Math.floor(l.height / (l.content.fontSize * l.content.lineHeight)));
      const capacity = lines * (l.width / (l.content.fontSize * 0.5));
      if (l.content.text.length > capacity * 1.05) out.push({ code: 'text_may_overflow', severity: 'warning', layerIds: [l.id], message: `ייתכן שהטקסט בשכבה ${l.id} לא נכנס בתיבה` });
    }
    if (l.role === 'logo' && opts.logoMinWidthPx && l.width < opts.logoMinWidthPx) {
      out.push({ code: 'logo_too_small', severity: 'warning', layerIds: [l.id], message: `רוחב הלוגו ${l.width}px קטן מהמינימום (${opts.logoMinWidthPx}px)` });
    }
  }
  const content = vis.filter((l) => ['logo', 'headline', 'subline', 'cta', 'disclaimer'].includes(l.role ?? ''));
  for (let i = 0; i < content.length; i++) for (let j = i + 1; j < content.length; j++) {
    const a = content[i], b = content[j];
    const ox = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
    const oy = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
    if (ox > 1 && oy > 1 && !(opts.allowOverlapRoles ?? []).includes(a.role!) ) {
      out.push({ code: 'overlap', severity: 'warning', layerIds: [a.id, b.id], message: `חפיפה בין ${a.id} ל-${b.id}` });
    }
  }
  return out;
}
